import { useEffect, useRef, useState } from 'react'
import { Button } from 'antd'
import { motion } from 'framer-motion'
import { QuizAnswers } from './QuizAnswers'
import { postMessageHandler, TIMESHIFT_SLIDE_END_PLAY } from '../../../utils'
import { useNotificationContext } from '../../../hooks/useNotificationContext'
import { ARIA_LIVE_MODES, MESSAGE_TYPE } from '../../../contexts/NotificationContext'
import { TRANSITION_TIME, transitionTemplate } from '../../../constants/constants'
import { BUTTON_SEND_SUFIX_QUIZ_ID, QUIZ_MODE_TYPE } from '../../../utils/video-helpers/types'

import { usePlayerContext, useVideoDataContext, useScheduledPlayerContext, useAnalyticContext } from '../../../hooks'

import './quiz-styles.less'

export const Quiz = (props) => {
  const { interactiveItem: quizData, onInteractivityFinished } = props
  const [chosenAnswers, setChosenAnswers] = useState({})
  const [highlightedAnswer, setHighlightedAnswer] = useState(null)

  const { videoSchedule } = useVideoDataContext()
  const { playHandler } = usePlayerContext()
  const { rewindOnSlideHandler } = useScheduledPlayerContext()
  const { showNotification } = useNotificationContext()
  const { playerBounding, videoReadyHandler, isVideoReady } = usePlayerContext()
  const { analyticCollector } = useAnalyticContext()

  const timerId = useRef(null)

  const isDisabledSubmit = !isVideoReady || quizData.answerState.length

  const onSubmitHandler = (event) => {
    event?.preventDefault()

    const chosenAnswersArray = Object.entries(chosenAnswers).reduce((array, [answerId, isChecked]) => {
      if (isChecked) array.push(answerId)
      return array
    }, [])

    if (!chosenAnswersArray.length) {
      showNotification(
        { content: 'Please select an answer!', key: 'select-answer' },
        'Please select an answer!',
        MESSAGE_TYPE.WARNING,
        ARIA_LIVE_MODES.ASSERTIVE,
      )
      return
    }
    const isCorrect = quizData.checkAnswers(chosenAnswersArray)

    analyticCollector.setAnswerClick(chosenAnswersArray, isCorrect, quizData.id)

    const nextData = isCorrect ? quizData.getOnCorrect() : quizData.getOnWrong()
    const { feedbackMessage } = nextData ?? {}
    if (feedbackMessage) {
      showNotification(
        { content: feedbackMessage, key: 'feedback-message' },
        feedbackMessage,
        isCorrect ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.ERROR,
        ARIA_LIVE_MODES.ASSERTIVE,
      )
    }
    videoReadyHandler(false)
    timerId.current = setTimeout(() => {
      const targetSlide = videoSchedule?.slidesMetaHashMap?.[nextData?.slide] ?? null
      if (!targetSlide) {
        onInteractivityFinished(() => playHandler(TIMESHIFT_SLIDE_END_PLAY))
      } else {
        onInteractivityFinished(() => rewindOnSlideHandler(nextData.slide))
      }
      videoReadyHandler(true)
    }, 2000)
  }
  const {
    viewObjects: { quizBackground, quizQuestion, quizSendButton },
  } = quizData
  const quizQuestionText = quizQuestion.text

  const keyHandler = (event) => {
    const isChooseOrSubmit = event.code === 'Space' || event.key === 'Enter'
    const isMovingTop = event.key === 'ArrowUp'
    const isMovingLeft = event.key === 'ArrowLeft'
    const isMovingBottom = event.key === 'ArrowDown'
    const isMovingRight = event.key === 'ArrowRight'
    const isSendButtonHighlighted = highlightedAnswer === BUTTON_SEND_SUFIX_QUIZ_ID

    document.activeElement.blur()

    // Space, Enter handlers
    if (isChooseOrSubmit) {
      // submit quiz
      if (isSendButtonHighlighted) return onSubmitHandler()

      // answer selected, adding to chosen answers
      if (highlightedAnswer && !isSendButtonHighlighted) {
        setChosenAnswers((checkedAnswers) => ({
          ...(quizData.quizMode === QUIZ_MODE_TYPE.MULTI_SELECT ? checkedAnswers : {}),
          [highlightedAnswer]: !checkedAnswers[highlightedAnswer],
        }))
        quizData.setAnswerState([])
        return
      }
    }

    // initial, first press of arrow key, highlighting first answer
    if (!highlightedAnswer) return setHighlightedAnswer(quizData.answerList[0])
    const currentIndex = quizData.answerList.findIndex((i) => i === highlightedAnswer)

    const total = quizData.answerList.length
    const cols = 3
    const rows = Math.ceil(total / cols)
    const row = Math.floor(currentIndex / cols) + 1
    const hasNewRow = rows - row > 0

    if (isMovingTop || isMovingLeft) {
      // first answer, selecting send button
      if (currentIndex === 0) return setHighlightedAnswer(BUTTON_SEND_SUFIX_QUIZ_ID)

      // send button selected, highlighting last answer
      if (isSendButtonHighlighted) return setHighlightedAnswer(quizData.answerList[total - 1])

      if (!quizData.isVerticalQuiz && isMovingTop) {
        let offset = currentIndex - cols < 0 ? 1 : cols
        // the only variant doesn't followed by common rules is to move from the forth element to a second element if quiz contains forth elements.
        // this is a simple workaround, cause we have a simple grid and if the grid will be different from 3x2, overall logic should be rewrote
        if (row === rows && currentIndex === 3 && total === 4) offset = 2
        return setHighlightedAnswer(quizData.answerList[Math.max(currentIndex - offset, 0)])
      }

      // changing to prev answer by default
      return setHighlightedAnswer(quizData.answerList[currentIndex - 1])
    }

    if (isMovingBottom || isMovingRight) {
      // if last element, highlight send button
      if (currentIndex === quizData.answerList.length - 1) return setHighlightedAnswer(BUTTON_SEND_SUFIX_QUIZ_ID)

      // if send button, highlight first answer
      if (isSendButtonHighlighted) return setHighlightedAnswer(quizData.answerList[0])

      if (!quizData.isVerticalQuiz && isMovingBottom) {
        const offset = hasNewRow ? cols : 1
        const newIndex = Math.min(currentIndex + offset, total - 1)
        return setHighlightedAnswer(quizData.answerList[newIndex])
      }

      // highlight next element by default
      return setHighlightedAnswer(quizData.answerList[currentIndex + 1])
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', keyHandler)
    window.addEventListener('message', postMessageHandler)
    return () => {
      document.removeEventListener('keydown', keyHandler)
      window.removeEventListener('message', postMessageHandler)
    }
  }, [highlightedAnswer, chosenAnswers])

  useEffect(() => {
    setChosenAnswers(quizData.answerState.reduce((obj, answerId) => ({ ...obj, [answerId]: true }), {}))
  }, [])

  return (
    <motion.form
      className="quiz-root"
      {...transitionTemplate}
      style={quizData.getElementStyles(playerBounding, quizBackground)}
      onSubmit={onSubmitHandler}
    >
      <h3 className="sr-only">Interactive Quiz</h3>
      <motion.h4
        {...transitionTemplate}
        transition={{ duration: TRANSITION_TIME + 0.2, delay: 0.4 }}
        className="quiz-question"
        style={quizData.getElementStyles(playerBounding, quizQuestion)}
      >
        <span className="sr-only">{quizQuestionText}</span>
      </motion.h4>
      <QuizAnswers
        quizQuestionText={quizQuestionText}
        quizData={quizData}
        chosenAnswers={chosenAnswers}
        setChosenAnswers={setChosenAnswers}
        highlightedAnswer={highlightedAnswer}
        setHighlightedAnswer={setHighlightedAnswer}
      />
      <motion.div {...transitionTemplate} transition={{ duration: TRANSITION_TIME + 0.2, delay: 0.4 }}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={isDisabledSubmit}
          className="quiz-submit-button"
          style={{
            display: 'flex',
            color: '#fff',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: quizSendButton.height * 0.2 * playerBounding.ratioY,
            fontSize: quizSendButton.height * 0.4 * playerBounding.ratioY,
            ...quizData.getElementStyles(playerBounding, {
              ...quizSendButton,
              isHighlighted: highlightedAnswer === BUTTON_SEND_SUFIX_QUIZ_ID,
            }),
            transform: 'scaleX(1) scaleY(1)',
          }}
        >
          <span className="sr-only">SUBMIT</span>
          <span style={{ position: 'relative' }}>Submit</span>
        </Button>
      </motion.div>
    </motion.form>
  )
}
